import {JSX, ReactNode} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {OwnerData} from "@/sections/destinations/welcome/parts/owned/owned";

import type {ContactPhoneItem} from "@/shared/ui/data-display/phone-number/phone-number";
import {PhoneNumber} from "@/shared/ui/data-display/phone-number/phone-number";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/owned-big.scss";

export type OwnedBigProps = {
    title?: string;
    owner: OwnerData;
    contactPhones?: Array<ContactPhoneItem>;
    children?: ReactNode;
};

export function OwnedBig({title, owner, contactPhones, children}: OwnedBigProps): JSX.Element {
    const {fullName, photo, position, tenure} = owner;
    const photoUrl = photo.url;
    const photoAlt = photo.alt || fullName;

    return (
        <div className="owned-big">
            <Typography as="h2" size={{desktop: 24, mobile: 18}} lineHeight={120} weight={600} className="owned-big__title">
                {title}
            </Typography>

            <div className="owned-big__wrap">
                <div className="owned-big__image">
                    <SmartImage src={photoUrl} alt={photoAlt} width={291} height={307} />
                </div>

                <div className="owned-big__content">
                    <Typography as="h3" size={{desktop: 20, mobile: 16}} lineHeight={110} weight={500} className="owned-big__name">
                        {fullName}
                    </Typography>

                    {contactPhones && contactPhones.length > 0 && (
                        <div className="owned-big__phones">
                            {contactPhones.map((item, index) => (
                                <PhoneNumber
                                    key={index}
                                    number={item.number}
                                    label={item.label}
                                    showLabel={false}
                                    iconSize={20}
                                    className="owned-big__phone-item"
                                />
                            ))}
                        </div>
                    )}

                    <div className="owned-big__info">
                        <Typography as="span" size={14} weight={600} className="owned-big__position">
                            {position}
                        </Typography>

                        <Typography as="span" size={14} weight={600} className="owned-big__honour">
                            {tenure}
                        </Typography>
                    </div>

                    {children && (
                        <Typography as="div" size={{desktop: 18, mobile: 14}} className="owned-big__description">
                            {children}
                        </Typography>
                    )}
                </div>
            </div>

            <SmartImage
                src={assetPath("/images/destinations/locally-owned.svg")}
                className="owned-big__badge"
                alt={title ?? ""}
                width={113}
                height={113}
            />
        </div>
    );
}
